import type { FormFlowStep } from '../../../../../types/formFlow';
import { formatCommittedDisplay } from './workerRegistryDisplay';
import type { WorkerRegistryCommittedEntry } from './workerRegistryTypes';
import { WORKER_REGISTRY_DONE_STEP_ID } from '../../../../../services/workerRegistryProgressApi';
import {
  workerRegistryHasWorkExperience,
  WORKER_REGISTRY_PREVIOUS_JOB_FIELD_NAMES,
} from './workerRegistrySteps';

function committedEntryForStep(
  step: FormFlowStep,
  stepIndex: number,
  answers: Record<string, string>,
  t: (key: string) => string,
  wheelLocale: string
): WorkerRegistryCommittedEntry {
  const raw = (answers[step.fieldName] ?? '').trim();
  return {
    stepIndex,
    stepId: step.stepId,
    fieldName: step.fieldName,
    displayText: formatCommittedDisplay(step, raw, t, wheelLocale) || raw,
    rawValue: raw,
  };
}

export function buildCommittedFromRestore(
  allSteps: FormFlowStep[],
  answers: Record<string, string>,
  currentStepId: string | null | undefined,
  t: (key: string) => string,
  wheelLocale: string
): { stepIndex: number; committed: WorkerRegistryCommittedEntry[]; isComplete: boolean } {
  if (!allSteps.length) {
    return { stepIndex: 0, committed: [], isComplete: false };
  }

  const includePrevious = workerRegistryHasWorkExperience(answers.constructionExperienceYears);
  const activeSteps = includePrevious
    ? allSteps
    : allSteps.filter(
        (s) =>
          !WORKER_REGISTRY_PREVIOUS_JOB_FIELD_NAMES.includes(
            s.fieldName as (typeof WORKER_REGISTRY_PREVIOUS_JOB_FIELD_NAMES)[number]
          )
      );

  if (currentStepId === WORKER_REGISTRY_DONE_STEP_ID) {
    const committed: WorkerRegistryCommittedEntry[] = [];
    for (let i = 0; i < activeSteps.length; i++) {
      const step = activeSteps[i];
      const fullIndex = allSteps.findIndex((s) => s.stepId === step.stepId);
      committed.push(
        committedEntryForStep(step, fullIndex >= 0 ? fullIndex : i, answers, t, wheelLocale)
      );
    }
    return { stepIndex: activeSteps.length, committed, isComplete: true };
  }

  let idx = currentStepId ? activeSteps.findIndex((s) => s.stepId === currentStepId) : -1;
  if (idx < 0 && currentStepId) {
    const onSkippedPreviousJob =
      !includePrevious &&
      allSteps.some(
        (s) =>
          s.stepId === currentStepId &&
          WORKER_REGISTRY_PREVIOUS_JOB_FIELD_NAMES.includes(
            s.fieldName as (typeof WORKER_REGISTRY_PREVIOUS_JOB_FIELD_NAMES)[number]
          )
      );
    if (onSkippedPreviousJob) {
      idx = activeSteps.length;
    } else {
      idx = 0;
    }
  }
  if (idx < 0) {
    idx = 0;
  }

  const committed: WorkerRegistryCommittedEntry[] = [];
  for (let i = 0; i < idx; i++) {
    const step = activeSteps[i];
    const fullIndex = allSteps.findIndex((s) => s.stepId === step.stepId);
    committed.push(
      committedEntryForStep(step, fullIndex >= 0 ? fullIndex : i, answers, t, wheelLocale)
    );
  }

  return {
    stepIndex: idx,
    committed,
    isComplete: false,
  };
}
